/**
 * Form-estimated skill rating derived from a capture session's pose_summary.
 *
 * THIS IS NOT AN OFFICIAL DUPR. DUPR comes from match results — court coverage,
 * shot selection, point-level tactics — none of which one clip can show. This is
 * a deliberately CONSERVATIVE estimate from stroke mechanics alone.
 *
 * ---- Why this was re-tuned (coach feedback, Sep 2026) ----
 *
 * A coach's 2.0-2.3 DUPR player was rated 3.9. Reproducing the old formula on
 * that session's stored payload gave exactly 3.902, from:
 *
 *   floor 2.0 + variety 0.60 + quality 0.90 + reps 0.40 = 3.90   (cap 4.5)
 *
 * Two things were wrong with the INPUTS, not just the arithmetic:
 *
 *   1. "Mechanical quality" (form_score/overall_score) does not discriminate
 *      skill. It is a weighted average of joint-angle bands like
 *      `swing_arm_extension: [120, 165]`, which almost any player lands inside —
 *      so a beginner and a 4.5 both score 80-90. Weighted at 0.45 it dominated
 *      the composite and pushed everyone up. Here it is weighted 0.20, and the
 *      honest signals dominate instead:
 *        - paddle_up_pct  — a 4.5+ keeps the paddle up on ~95% of intervals;
 *                           a 2.0 player sits near 40%. Genuinely discriminative.
 *        - consistency    — rally-to-rally repeatability.
 *   2. Stroke variety credited strokes the coach never asked for. The coach
 *      typed "Transition Zone"; the focus was not recognised, so dinks, drops
 *      and drives all counted toward variety and unlocked the full +0.6.
 *      Variety now counts ONLY stroke types inside the session's focus.
 *
 * ---- How the rating is built ----
 *
 *   skill (0-100) = weighted average of the available honest signals
 *   rating        = a band lookup on skill, then adjusted by play mode,
 *                   then clamped by explicit gates
 *
 * Every cap that fires is reported in `reasons`, so a coach can see WHY the
 * number is what it is instead of trusting a black box.
 *
 * Returns { rating, confidence, band, reps, play_mode, context, reasons,
 *           coach_stroke, focus, components } — or null when there is not
 * enough signal to estimate at all.
 */

// Signal weights. Renormalised over whichever inputs are actually present.
const W_PADDLE = 0.30;   // paddle-up % — the most honest skill tell
const W_READY = 0.15;    // ready-position composite (partly inflated)
const W_CONSIST = 0.25;  // repeatability
const W_STROKE = 0.20;   // on-focus stroke quality (weak but non-zero signal)
const W_REPS = 0.10;     // sample size

// skill -> estimated rating. Deliberately compressed at the top: this is a
// single controlled clip, not a tournament record.
//
// Round 2 (coach feedback): shifted down ~0.3 across the board. The gate below
// was doing all the work — the same session scored a 2.9 skill index (3.0 with
// variety) and was only pulled to 2.3 by the paddle cap. A rating that depends
// on one gate to be honest is not calibrated; it is masked. If the bands say
// 2.9 for a 2.0-2.3 player, the bands are wrong.
const BANDS = [
  [92, 3.6],
  [85, 3.3],
  [75, 3.0],
  [65, 2.7],
  [55, 2.5],
  [45, 2.3],
  [35, 2.1],
  [0, 2.0],
];

// The paddle-up gate. Paddle down between shots is the single most reliable
// beginner tell, and it is measured directly rather than inferred.
//
// Round 2: tightened, so a sub-50% player lands at the floor rather than the
// middle of the beginner band.
const PADDLE_GATES = [
  [50, 2.1],   // under half the intervals with the paddle up — cannot be a 3.0+
  [70, 2.5],
  [85, 2.8],
];

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function _weightedAvg(parts) {
  let sum = 0;
  let total = 0;
  for (const [val, w] of parts) {
    if (val == null || Number.isNaN(val)) continue;
    sum += val * w;
    total += w;
  }
  return total > 0 ? sum / total : null;
}

function _bandFor(skill) {
  for (const [floor, rating] of BANDS) {
    if (skill >= floor) return rating;
  }
  return 2.0;
}

export function estimateFormRating(poseSummary, playMode = "drill") {
  if (!poseSummary || typeof poseSummary !== "object") return null;
  const pb = poseSummary.pickleball_stats || {};
  const ready = pb.ready_position || {};
  const strokes = pb.stroke_breakdown || {};

  // Coach-typed context (parsed server-side when the session was saved). Carries
  // the drill focus — e.g. "Transition Zone" -> focus_strokes
  // ["volley","block","drive","half-volley"] — so off-focus strokes do not earn
  // variety credit.
  const coachCtx = poseSummary.coach_context || null;
  const coachStroke = coachCtx?.stroke_type || null;
  const focusLabel = coachCtx?.focus_label || null;
  const focusStrokes = Array.isArray(coachCtx?.focus_strokes) ? coachCtx.focus_strokes : null;

  // form_score/overall_score — the non-discriminative quality score (see header).
  const formScore = poseSummary.form_score ?? poseSummary.overall_score;
  const consistency = poseSummary.consistency;
  const readyScore = ready.ready_score;
  const paddleUp = ready.paddle_up_pct;
  const reps = poseSummary.rep_count ?? (poseSummary.reps?.length || 0);

  const reasons = [];

  // ---- Stroke counts, split by whether they are inside the session's focus ----
  let onFocusTypes = 0;
  let onFocusBlend = null;
  let offFocusTypes = 0;
  let allTypes = 0;
  if (strokes && typeof strokes === "object") {
    let s = 0;
    let w = 0;
    for (const [kind, e] of Object.entries(strokes)) {
      const cnt = Number(e?.count || 0);
      const avg = Number(e?.avg_score || 0);
      // A stroke type with a single rep is classifier noise, not evidence.
      if (cnt < 2 || avg <= 0) continue;
      allTypes += 1;
      const inFocus = !focusStrokes || focusStrokes.includes(kind);
      if (inFocus) {
        onFocusTypes += 1;
        s += avg * cnt;
        w += cnt;
      } else {
        offFocusTypes += 1;
      }
    }
    if (w > 0) onFocusBlend = s / w;
  }

  // A single-stroke drill gets no variety credit at all.
  const varietyTypes = coachStroke ? 0 : onFocusTypes;

  // Need at least 3 reps OR a quality score to estimate anything.
  if (reps < 3 && formScore == null) return null;

  const skill = _weightedAvg([
    [paddleUp, W_PADDLE],
    [readyScore, W_READY],
    [consistency, W_CONSIST],
    [onFocusBlend, W_STROKE],
    [reps > 0 ? Math.min(100, (reps / 40) * 100) : null, W_REPS],
  ]);
  if (skill == null) return null;

  const isGame = playMode === "game" || playMode === "match";

  // ---- Base from the band lookup ----
  let rating = _bandFor(skill);

  // ---- Variety: small, and only on-focus, and NOT for a focused drill ----
  // 2 on-focus types -> +0.1, 3 -> +0.2, 4+ -> +0.3. Nothing for 1.
  const varietyBonus = isGame
    ? Math.min(0.3, Math.max(0, varietyTypes - 1) * 0.1) + 0.1
    : Math.min(0.3, Math.max(0, varietyTypes - 1) * 0.1);
  rating += varietyBonus;
  if (varietyTypes >= 2) {
    reasons.push(`${varietyTypes} on-focus stroke types (+${varietyBonus.toFixed(1)})`);
  }

  // ---- Gates ----
  // 1. Paddle-up. Applied to the band result, not the skill score, so it caps
  //    the claim rather than silently re-weighting the inputs.
  if (paddleUp != null) {
    for (const [below, cap] of PADDLE_GATES) {
      if (paddleUp < below) {
        if (rating > cap) {
          reasons.push(`paddle up only ${Math.round(paddleUp)}% of intervals — capped at ${cap.toFixed(1)}`);
          rating = cap;
        }
        break;
      }
    }
  }

  // 2. Sample size.
  if (reps < 8) {
    const cap = 2.8;
    if (rating > cap) {
      reasons.push(`${reps} reps — too few to justify more (cap ${cap.toFixed(1)})`);
      rating = cap;
    }
  } else if (reps < 20) {
    const cap = 3.2;
    if (rating > cap) {
      reasons.push(`${reps} reps — capped at ${cap.toFixed(1)}`);
      rating = cap;
    }
  }

  // 3. Ceiling. A controlled drill cannot demonstrate DUPR; only game/match
  //    footage can justify the top of the range.
  const ceiling = isGame ? 4.5 : 3.5;
  if (rating > ceiling) {
    reasons.push(`${isGame ? "game/match" : "drill"} clip — ceiling ${ceiling.toFixed(1)}`);
    rating = ceiling;
  }

  // 4. A focused session: the player only showed one part of the game, so the
  //    estimate stays modest regardless of how clean it looked.
  if ((focusLabel || coachStroke) && !isGame) {
    const cap = 3.0;
    if (rating > cap) {
      reasons.push(`focused ${focusLabel || coachStroke} session — ceiling ${cap.toFixed(1)}`);
      rating = cap;
    }
  }

  // Floor: never below 2.0 (this is the entry point of the scale).
  if (rating < 2.0) rating = 2.0;
  rating = +clamp(rating, 2.0, 4.5).toFixed(1);

  // ---- Confidence ----
  let band, label;
  if (reps >= 20 && varietyTypes >= 3) {
    band = 0.4; label = "medium";
  } else if (reps >= 8) {
    band = 0.5; label = "low";
  } else {
    band = 0.7; label = "low";
  }

  // ---- Human-readable context ----
  let context = null;
  if (coachStroke) {
    const handLabel = coachCtx?.hand ? `${coachCtx.hand} ` : "";
    context = `Coach-anchored drill: ${handLabel}${coachStroke}. Estimated from mechanics only — not a DUPR.`;
  } else if (focusLabel) {
    context = `Focused on ${focusLabel} — rated on that part of the game only. Not a DUPR.`;
  } else if (reps < 8) {
    context = "Short clip — conservative cap until more reps are captured.";
  } else {
    context = "Estimated from stroke mechanics alone — not a DUPR.";
  }
  // Surface a focus mismatch: the coach named an area but nothing in it was
  // detected, which is itself useful feedback (and means no variety credit).
  if (focusStrokes && onFocusTypes === 0 && offFocusTypes > 0) {
    context += ` No ${focusLabel || "on-focus"} strokes were detected — the clip may not show the intended work.`;
  }

  return {
    rating,
    confidence: label,
    band,
    reps,
    play_mode: playMode,
    context,
    reasons,
    coach_stroke: coachStroke,
    focus: focusLabel,
    components: {
      paddle_up_pct: paddleUp != null ? Math.round(paddleUp) : null,
      ready_score: readyScore != null ? Math.round(readyScore) : null,
      consistency: consistency != null ? Math.round(consistency) : null,
      stroke_blend: onFocusBlend != null ? +onFocusBlend.toFixed(1) : null,
      form_score: formScore != null ? Math.round(formScore) : null,
      skill_index: +skill.toFixed(1),
      stroke_types: varietyTypes,
      stroke_types_on_focus: onFocusTypes,
      stroke_types_off_focus: offFocusTypes,
      stroke_types_detected: allTypes,
      ceiling,
    },
  };
}
